import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'sv' | 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  sv: {
    home: 'Hem',
    shop: 'Butik',
    about: 'Om Oss',
    contact: 'Kontakt',
    cart: 'Varukorg',
    proceedToCheckout: 'Gå till kassan',
    addToCart: 'Lägg till i varukorg',
    viewProduct: 'Visa produkt',
    ourStory: 'Vår Berättelse',
    featuredProducts: 'Utvalda Produkter',
    shopNow: 'Handla Nu',
    discoverNature: 'Upptäck naturens lyx',
    heroSubtitle: '100% organisk hudvård från Libanons hjärta',
    brandStoryTitle: 'VÅR BERÄTTELSE',
    brandStory: 'Khan Al Saboun, ett legendariskt libanesiskt märke med mer än 600 års historia, härstammar från de gamla souqerna i Tripoli, där hantverkare perfektionerade konsten att tillverka naturlig tvål med lokala ingredienser som olivolja och aromatiska örter. Återuppliven av Dr. Bader Hassoun, har märket bevarat sina traditionella metoder samtidigt som det utvecklats till en global symbol för renhet och lyx. Idag erbjuder Khan Al Saboun en exklusiv kollektion av handgjorda hudvårdsprodukter, hyllade över hela världen för deras äkthet och kvalitet, och förkroppsligar det tidlösa arvet från libanesiskt hantverk.',
    ourValues: 'Våra Värderingar',
    organicTitle: 'Organiskt',
    organicDescription: '100% naturliga ingredienser, inga kemikalier eller konserveringsmedel',
    heritageTitle: 'Arv',
    heritageDescription: '600+ år av traditionellt libanesiskt hantverk',
    sustainableTitle: 'Hållbart',
    sustainableDescription: 'Miljövänlig produktion och ansvarsfull anskaffning',
    ourMission: 'Vårt Uppdrag',
    missionText1: 'På Càivonclub tror vi på naturens kraft. Vårt uppdrag är att erbjuda dig de finaste ekologiska hudvårdsprodukterna från Libanons mest prestigefyllda arvsmärke, Khan Al Saboun. Varje produkt berättar en historia om tradition, renhet och tidlösa skönhetsritualer som gått i arv genom generationer.',
    missionText2: 'Vi är engagerade i hållbarhet, äkthet och att leverera lyx som respekterar både din hud och vår planet.',
    emailLabel: 'E-post',
    phoneLabel: 'Telefon',
    addressLabel: 'Adress',
    businessHours: 'Öppettider',
    mondayFriday: 'Måndag - Fredag',
    saturday: 'Lördag',
    sunday: 'Söndag',
    closed: 'Stängt',
    whyOrganic: 'Varför Organiskt?',
    organicBenefits: 'Våra produkter är tillverkade med 100% naturliga ingredienser, fria från kemikalier och konserveringsmedel. Varje produkt är handgjord med passion och dedikation.',
    filterByCategory: 'Filtrera efter kategori',
    filterByPrice: 'Filtrera efter pris',
    sortBy: 'Sortera efter',
    priceRange: 'Prisintervall',
    productsFound: 'produkter hittade',
    addedToCart: 'Tillagd i varukorgen',
    ingredients: 'Ingredienser',
    benefits: 'Fördelar',
    howToUse: 'Användning',
    scentProfile: 'Doftprofil',
    productDescription: 'Produktbeskrivning',
    categories: 'Kategorier',
    all: 'Alla',
    soaps: 'Tvålar',
    oils: 'Oljor',
    serums: 'Serum',
    bodyCare: 'Kroppsvård',
    hairCare: 'Hårvård',
    giftSets: 'Presentset',
    contactUs: 'Kontakta Oss',
    getInTouch: 'Vi älskar att höra från dig. Skicka oss ett meddelande så återkommer vi så snart som möjligt.',
    name: 'Namn',
    email: 'E-post',
    message: 'Meddelande',
    send: 'Skicka',
    followUs: 'Följ Oss',
    customerService: 'Kundservice',
    information: 'Information',
    privacyPolicy: 'Integritetspolicy',
    termsConditions: 'Villkor',
    returnRefund: 'Retur & Återbetalning',
    cookiePolicy: 'Cookiepolicy',
    allRightsReserved: 'Alla rättigheter förbehållna',
    // 404 Page
    pageNotFound: 'Sidan hittades inte',
    pageNotFoundMessage: 'Oj! Sidan du letar efter finns inte.',
    returnToHome: 'Tillbaka till startsidan',
    // Accessibility
    openCart: 'Öppna varukorg',
    openMenu: 'Öppna meny',
    closeMenu: 'Stäng meny',
    changeLanguage: 'Byt språk',
    searchProducts: 'Sök produkter',
    previousImage: 'Föregående bild',
    nextImage: 'Nästa bild',
    closeDialog: 'Stäng',
    previousPage: 'Föregående sida',
    nextPage: 'Nästa sida',
    goToPage: 'Gå till sida',
    cookieConsent: 'Godkänn cookies',
    // Admin translations
    adminPanel: 'Adminpanel',
    storeManager: 'Butiksansvarig',
    limitedAccess: 'Begränsad Åtkomst',
    dashboard: 'Instrumentpanel',
    products: 'Produkter',
    stockManager: 'Lagerhantering',
    ordersMenu: 'Beställningar',
    banner: 'Banner',
    siteInfo: 'Webbplatsinformation',
    userManagement: 'Användarhantering',
    homepage: 'Startsida',
    contentManager: 'Innehållshanterare',
    designEditor: 'Designredigerare',
    reviewsMenu: 'Recensioner',
    statusMessages: 'Statusmeddelanden',
    emailSettings: 'E-postinställningar',
    shippingSettings: 'Fraktinställningar',
    signOut: 'Logga ut',
    // Product Manager
    manageProductCatalog: 'Hantera din produktkatalog',
    allProducts: 'Alla Produkter',
    allDiscounts: 'Alla Rabatter',
    addProduct: 'Lägg till Produkt',
    editProduct: 'Redigera Produkt',
    deleteProduct: 'Ta bort Produkt',
    confirmDelete: 'Är du säker? Detta kan inte ångras.',
    featured: 'Utvald',
    newArrival: 'Nyhet',
    outOfStock: 'Slut i lager',
    inStock: 'I lager',
    edit: 'Redigera',
    delete: 'Ta bort',
    // Category Manager
    manageCategories: 'Hantera produktkategorier',
    addCategory: 'Lägg till Kategori',
    editCategory: 'Redigera Kategori',
    categoryName: 'Kategorinamn',
    createCategory: 'Skapa Kategori',
    updateCategory: 'Uppdatera Kategori',
    cancel: 'Avbryt',
    unassignedProducts: 'Otilldelade Produkter',
    assignCategory: 'Tilldela kategori',
    // Stock Manager
    manageInventory: 'Hantera inventarier och lagernivåer',
    monitorManageInventory: 'Övervaka och hantera produktlagernivåer',
    totalProducts: 'Totalt Produkter',
    lowStock: 'Lågt Lager',
    updateStock: 'Uppdatera Lager',
    currentStock: 'Nuvarande Lager',
    lowStockThreshold: 'Tröskelvärde för lågt lager',
    save: 'Spara',
    orders: {
      title: 'Orderhantering',
      searchPlaceholder: 'Sök ordernummer eller e-post...',
      allStatuses: 'Alla statusar',
      statusPending: 'Väntande',
      statusInProgress: 'Pågår',
      statusShipped: 'Skickad',
      fetchError: 'Kunde inte hämta beställningar',
      statusUpdated: 'Orderstatus uppdaterad',
      updateError: 'Kunde inte uppdatera status',
      noOrders: 'Inga beställningar hittades',
      orderNumber: 'Ordernummer',
      customer: 'Kund',
      total: 'Totalt',
      status: 'Status',
      viewDetails: 'Visa detaljer',
      createdAt: 'Skapad',
      orderDetails: 'Orderdetaljer',
      customerInfo: 'Kundinformation',
      name: 'Namn',
      email: 'E-post',
      phone: 'Telefon',
      address: 'Adress',
      items: 'Varor',
      product: 'Produkt',
      quantity: 'Antal',
      price: 'Pris',
      subtotal: 'Delsumma',
    },
    // User Management
    manageAdminsStoreManagers: 'Hantera administratörer och butiksansvariga',
    addUser: 'Lägg till användare',
    admins: 'Administratörer',
    storeManagers: 'Butiksansvariga',
    added: 'Tillagd',
    resetPassword: 'Återställ lösenord',
    accessLevels: 'Åtkomstnivåer',
    adminsAccessDesc: 'Full åtkomst till alla funktioner inklusive användarhantering, webbplatsinställningar och innehållshantering.',
    storeManagersAccessDesc: 'Kan hantera produkter, beställningar och lager. Kan inte komma åt användarhantering eller webbplatsinställningar.',
    protected: 'Skyddad',
    // Banner Manager
    bannerManager: 'Bannerhantering',
    managePromotionalBanner: 'Hantera kampanjbanner',
    bannerSettings: 'Bannerinställningar',
    enableBanner: 'Aktivera banner',
    bannerImageUrl: 'Banner-bild URL',
    bannerText: 'Bannertext',
    enterBannerText: 'Ange bannertext',
    // Site Information
    siteInformation: 'Webbplatsinformation',
    manageContactSocialInfo: 'Hantera kontakt- och sociala mediainformation',
    contactInformation: 'Kontaktinformation',
    whatsappNumber: 'WhatsApp-nummer',
    socialMediaLinks: 'Sociala medialänkar',
    // Common
    loading: 'Laddar...',
    search: 'Sök',
    filter: 'Filtrera',
    actions: 'Åtgärder',
    swedish: 'Svenska',
    english: 'English',
    common: {
      loading: 'Laddar...',
      sending: 'Skickar...',
      backToHome: 'Tillbaka till Startsidan',
    },
    // Product Guide Assistant
    smartProductGuide: 'Smart produktguide',
    smartProductGuideText: 'Låt vår AI-rådgivare hjälpa dig hitta de produkter som passar just dig, din hud och ditt hår.',
    startSmartProductGuide: 'Starta smart produktguide',
    resetChat: 'Återställ chatt',
    typeYourMessage: 'Skriv ditt meddelande...',
    // Order Tracking
    trackYourOrder: 'Spåra Din Order',
    trackYourOrderDescription: 'Ange din e-postadress och ordernummer för att se orderdetaljer',
    enterOrderDetails: 'Ange Orderdetaljer',
    enterOrderDetailsDescription: 'Vi behöver din e-post och ordernummer för att hitta din beställning',
    orderNumberPlaceholder: 'T.ex. ORD-12345',
    emailPlaceholder: 'din@epost.se',
    trackOrder: 'Spåra Order',
    orderNotFound: 'Order Hittades Inte',
    orderNotFoundDescription: 'Ingen order hittades med denna e-post och ordernummer. Kontrollera att informationen är korrekt.',
    orderFound: 'Order Hittad',
    orderFoundDescription: 'Din beställning har hittats och laddats',
    trackOrderError: 'Ett fel uppstod när ordern skulle spåras',
    orderStatus: 'Orderstatus',
    orderDate: 'Orderdatum',
    contactAboutOrder: 'Kontakta Oss Angående Denna Order',
    contactAboutOrderDescription: 'Har du frågor om din beställning? Skicka oss ett meddelande.',
    sendMessage: 'Skicka Meddelande',
    subject: 'Ämne',
    subjectPlaceholder: 'T.ex. Fråga om leverans',
    messagePlaceholder: 'Skriv ditt meddelande här...',
    optional: 'valfritt',
    messageSent: 'Meddelande Skickat',
    messageSentDescription: 'Vi har mottagit ditt meddelande och återkommer så snart som möjligt.',
    messageSendError: 'Kunde inte skicka meddelandet',
    contactMessageRequired: 'Meddelandet kan inte vara tomt',
    trackOrderFillAllFields: 'Vänligen fyll i både e-post och ordernummer',
    invalidEmailFormat: 'Ogiltig e-postadress',
    orderArchivedMessage: 'Denna order är arkiverad eller äldre än 30 dagar. För support, vänligen kontakta oss direkt på support@caivonclub.se',
    currency: 'kr',
    error: 'Fel',
    // Reviews
    reviews: {
      leaveReview: 'Lämna Recension',
      reviewingProduct: 'Du recenserar',
      yourRating: 'Ditt betyg',
      yourComment: 'Din kommentar',
      commentPlaceholder: 'Berätta om din upplevelse med produkten...',
      charactersMin: 'tecken minst',
      submitReview: 'Skicka Recension',
      thankYou: 'Tack för din recension!',
      submitError: 'Kunde inte skicka recensionen',
      selectRating: 'Välj ett betyg',
      commentTooShort: 'Kommentaren måste vara minst 10 tecken',
      invalidLink: 'Ogiltig Recension Länk',
      invalidLinkDesc: 'Denna recension länk är ogiltig eller har redan använts.',
      verifiedPurchase: 'Verifierat Köp',
      customerReviews: 'Kundrecensioner',
      noReviews: 'Inga recensioner än',
      beFirst: 'Bli den första att recensera denna produkt!',
      basedOn: 'Baserat på',
      review: 'recension',
      reviewsPlural: 'recensioner',
    },
  },
  en: {
    home: 'Home',
    shop: 'Shop',
    about: 'About',
    contact: 'Contact',
    cart: 'Cart',
    proceedToCheckout: 'Proceed to Checkout',
    addToCart: 'Add to Cart',
    viewProduct: 'View Product',
    ourStory: 'Our Story',
    featuredProducts: 'Featured Products',
    shopNow: 'Shop Now',
    discoverNature: 'Discover nature\'s luxury',
    heroSubtitle: '100% organic skincare from the heart of Lebanon',
    brandStoryTitle: 'OUR STORY',
    brandStory: 'Khan Al Saboun, a legendary Lebanese brand with more than 600 years of history, originates from the ancient souks of Tripoli, where artisans perfected the craft of natural soapmaking using local ingredients such as olive oil and aromatic herbs. Revitalized by Dr. Bader Hassoun, the brand has preserved its traditional methods while evolving into a global symbol of purity and luxury. Today, Khan Al Saboun offers an exclusive collection of handcrafted skincare products, celebrated worldwide for their authenticity and quality, embodying the timeless heritage of Lebanese craftsmanship.',
    ourValues: 'Our Values',
    organicTitle: 'Organic',
    organicDescription: '100% natural ingredients, no chemicals or preservatives',
    heritageTitle: 'Heritage',
    heritageDescription: '600+ years of traditional Lebanese craftsmanship',
    sustainableTitle: 'Sustainable',
    sustainableDescription: 'Eco-friendly production and responsible sourcing',
    ourMission: 'Our Mission',
    missionText1: 'At Càivonclub, we believe in the power of nature. Our mission is to bring you the finest organic skincare products from Lebanon\'s most prestigious heritage brand, Khan Al Saboun. Every product tells a story of tradition, purity, and timeless beauty rituals passed down through generations.',
    missionText2: 'We are committed to sustainability, authenticity, and delivering luxury that respects both your skin and our planet.',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    addressLabel: 'Address',
    businessHours: 'Business Hours',
    mondayFriday: 'Monday - Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    closed: 'Closed',
    whyOrganic: 'Why Organic?',
    organicBenefits: 'Our products are made with 100% natural ingredients, free from chemicals and preservatives. Each product is handcrafted with passion and dedication.',
    filterByCategory: 'Filter by category',
    filterByPrice: 'Filter by price',
    sortBy: 'Sort by',
    priceRange: 'Price range',
    productsFound: 'products found',
    addedToCart: 'Added to cart',
    ingredients: 'Ingredients',
    benefits: 'Benefits',
    howToUse: 'How to Use',
    scentProfile: 'Scent Profile',
    productDescription: 'Product Description',
    categories: 'Categories',
    all: 'All',
    soaps: 'Soaps',
    oils: 'Oils',
    serums: 'Serums',
    bodyCare: 'Body Care',
    hairCare: 'Hair Care',
    giftSets: 'Gift Sets',
    contactUs: 'Contact Us',
    getInTouch: 'We love to hear from you. Send us a message and we\'ll get back to you as soon as possible.',
    name: 'Name',
    email: 'Email',
    message: 'Message',
    send: 'Send',
    followUs: 'Follow Us',
    customerService: 'Customer Service',
    information: 'Information',
    privacyPolicy: 'Privacy Policy',
    termsConditions: 'Terms & Conditions',
    returnRefund: 'Return & Refund',
    cookiePolicy: 'Cookie Policy',
    allRightsReserved: 'All rights reserved',
    // 404 Page
    pageNotFound: 'Page Not Found',
    pageNotFoundMessage: 'Oops! The page you are looking for does not exist.',
    returnToHome: 'Return to Home',
    // Accessibility
    openCart: 'Open cart',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    changeLanguage: 'Change language',
    searchProducts: 'Search products',
    previousImage: 'Previous image',
    nextImage: 'Next image',
    closeDialog: 'Close',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    goToPage: 'Go to page',
    cookieConsent: 'Cookie consent',
    // Admin translations
    adminPanel: 'Admin Panel',
    storeManager: 'Store Manager',
    limitedAccess: 'Limited Access',
    dashboard: 'Dashboard',
    products: 'Products',
    stockManager: 'Stock Manager',
    ordersMenu: 'Orders',
    orders: {
      title: 'Order Management',
      searchPlaceholder: 'Search order number or email...',
      allStatuses: 'All Statuses',
      statusPending: 'Pending',
      statusInProgress: 'In Progress',
      statusShipped: 'Shipped',
      fetchError: 'Failed to fetch orders',
      statusUpdated: 'Order status updated',
      updateError: 'Failed to update status',
      noOrders: 'No orders found',
      orderNumber: 'Order Number',
      customer: 'Customer',
      total: 'Total',
      status: 'Status',
      viewDetails: 'View Details',
      createdAt: 'Created',
      orderDetails: 'Order Details',
      customerInfo: 'Customer Information',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      address: 'Address',
      items: 'Items',
      product: 'Product',
      quantity: 'Quantity',
      price: 'Price',
      subtotal: 'Subtotal',
    },
    banner: 'Banner',
    siteInfo: 'Site Info',
    userManagement: 'User Management',
    homepage: 'Homepage',
    contentManager: 'Content Manager',
    designEditor: 'Design Editor',
    reviewsMenu: 'Reviews',
    statusMessages: 'Status Messages',
    emailSettings: 'Email Settings',
    shippingSettings: 'Shipping Settings',
    signOut: 'Sign Out',
    // Product Manager
    manageProductCatalog: 'Manage your product catalog',
    allProducts: 'All Products',
    allDiscounts: 'All Discounts',
    addProduct: 'Add Product',
    editProduct: 'Edit Product',
    deleteProduct: 'Delete Product',
    confirmDelete: 'Are you sure? This cannot be undone.',
    featured: 'Featured',
    newArrival: 'New Arrival',
    outOfStock: 'Out of Stock',
    inStock: 'In Stock',
    edit: 'Edit',
    delete: 'Delete',
    // Category Manager
    manageCategories: 'Manage product categories',
    addCategory: 'Add Category',
    editCategory: 'Edit Category',
    categoryName: 'Category Name',
    createCategory: 'Create Category',
    updateCategory: 'Update Category',
    cancel: 'Cancel',
    unassignedProducts: 'Unassigned Products',
    assignCategory: 'Assign category',
    // Stock Manager
    manageInventory: 'Manage inventory and stock levels',
    monitorManageInventory: 'Monitor and manage product inventory levels',
    totalProducts: 'Total Products',
    lowStock: 'Low Stock',
    updateStock: 'Update Stock',
    currentStock: 'Current Stock',
    lowStockThreshold: 'Low Stock Threshold',
    save: 'Save',
    // User Management
    manageAdminsStoreManagers: 'Manage admins and store managers',
    addUser: 'Add User',
    admins: 'Admins',
    storeManagers: 'Store Managers',
    added: 'Added',
    resetPassword: 'Reset Password',
    accessLevels: 'Access Levels',
    adminsAccessDesc: 'Full access to all features including user management, site settings, and content management.',
    storeManagersAccessDesc: 'Can manage products, orders, and inventory. Cannot access user management or site settings.',
    protected: 'Protected',
    // Banner Manager
    bannerManager: 'Banner Manager',
    managePromotionalBanner: 'Manage promotional banner',
    bannerSettings: 'Banner Settings',
    enableBanner: 'Enable Banner',
    bannerImageUrl: 'Banner Image URL',
    bannerText: 'Banner Text',
    enterBannerText: 'Enter banner text',
    // Site Information
    siteInformation: 'Site Information',
    manageContactSocialInfo: 'Manage contact and social information',
    contactInformation: 'Contact Information',
    whatsappNumber: 'WhatsApp Number',
    socialMediaLinks: 'Social Media Links',
    // Common
    allOrders: 'All Orders',
    noOrdersFound: 'No orders found',
    placedOn: 'Placed on',
    customerInformation: 'Customer Information',
    orderInformation: 'Order Information',
    saveChanges: 'Save Changes',
    common: {
      loading: 'Loading...',
      sending: 'Sending...',
      backToHome: 'Back to Home',
    },
    // Order Manager
    manageOrders: 'Manage customer orders',
    orderNumber: 'Order Number',
    customer: 'Customer',
    status: 'Status',
    total: 'Total',
    date: 'Date',
    viewDetails: 'View Details',
    orderDetails: 'Order Details',
    customerInfo: 'Customer Information',
    phone: 'Phone',
    address: 'Address',
    orderItems: 'Order Items',
    quantity: 'Quantity',
    price: 'Price',
    updateStatus: 'Update Status',
    pending: 'Pending',
    processing: 'Processing',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    // Common
    loading: 'Loading...',
    search: 'Search',
    filter: 'Filter',
    actions: 'Actions',
    swedish: 'Svenska',
    english: 'English',
    // Product Guide Assistant
    smartProductGuide: 'Smart Product Guide',
    smartProductGuideText: 'Let our AI assistant help you find the products that best match your skin, hair and preferences.',
    startSmartProductGuide: 'Start Smart Product Guide',
    resetChat: 'Reset chat',
    typeYourMessage: 'Type your message...',
    // Order Tracking
    trackYourOrder: 'Track Your Order',
    trackYourOrderDescription: 'Enter your email and order number to view order details',
    enterOrderDetails: 'Enter Order Details',
    enterOrderDetailsDescription: 'We need your email and order number to find your order',
    orderNumberPlaceholder: 'e.g. ORD-12345',
    emailPlaceholder: 'your@email.com',
    trackOrder: 'Track Order',
    orderNotFound: 'Order Not Found',
    orderNotFoundDescription: 'No order found with this email and order number. Please check that the information is correct.',
    orderFound: 'Order Found',
    orderFoundDescription: 'Your order has been found and loaded',
    trackOrderError: 'An error occurred while tracking the order',
    orderStatus: 'Order Status',
    orderDate: 'Order Date',
    contactAboutOrder: 'Contact Us About This Order',
    contactAboutOrderDescription: 'Have questions about your order? Send us a message.',
    sendMessage: 'Send Message',
    subject: 'Subject',
    subjectPlaceholder: 'e.g. Question about delivery',
    messagePlaceholder: 'Write your message here...',
    optional: 'optional',
    messageSent: 'Message Sent',
    messageSentDescription: 'We have received your message and will get back to you as soon as possible.',
    messageSendError: 'Could not send message',
    contactMessageRequired: 'Message cannot be empty',
    trackOrderFillAllFields: 'Please fill in both email and order number',
    invalidEmailFormat: 'Invalid email address',
    orderArchivedMessage: 'This order is archived or older than 30 days. For support, please contact us directly at support@caivonclub.se',
    currency: 'SEK',
    error: 'Error',
    // Reviews
    reviews: {
      leaveReview: 'Leave Review',
      reviewingProduct: 'You are reviewing',
      yourRating: 'Your rating',
      yourComment: 'Your comment',
      commentPlaceholder: 'Tell us about your experience with the product...',
      charactersMin: 'characters minimum',
      submitReview: 'Submit Review',
      thankYou: 'Thank you for your review!',
      submitError: 'Could not submit review',
      selectRating: 'Please select a rating',
      commentTooShort: 'Comment must be at least 10 characters',
      invalidLink: 'Invalid Review Link',
      invalidLinkDesc: 'This review link is invalid or has already been used.',
      verifiedPurchase: 'Verified Purchase',
      customerReviews: 'Customer Reviews',
      noReviews: 'No reviews yet',
      beFirst: 'Be the first to review this product!',
      basedOn: 'Based on',
      review: 'review',
      reviewsPlural: 'reviews',
    },
  },
  ar: {
    home: 'الرئيسية',
    shop: 'المتجر',
    about: 'من نحن',
    contact: 'اتصل بنا',
    cart: 'السلة',
    proceedToCheckout: 'المتابعة إلى الدفع',
    addToCart: 'أضف إلى السلة',
    viewProduct: 'عرض المنتج',
    ourStory: 'قصتنا',
    featuredProducts: 'منتجات مميزة',
    shopNow: 'تسوق الآن',
    discoverNature: 'اكتشف فخامة الطبيعة',
    heroSubtitle: 'منتجات عناية بالبشرة عضوية 100% من قلب لبنان',
    brandStoryTitle: 'قصتنا',
    brandStory: 'خان الصابون، علامة تجارية لبنانية أسطورية يمتد تاريخها لأكثر من 600 عام، نشأت من أسواق طرابلس القديمة، حيث أتقن الحرفيون فن صناعة الصابون الطبيعي باستخدام مكونات محلية مثل زيت الزيتون والأعشاب العطرية. أعاد الدكتور بدر حسون إحياء العلامة التجارية، التي حافظت على أساليبها التقليدية بينما تطورت لتصبح رمزًا عالميًا للنقاء والفخامة. اليوم، يقدم خان الصابون مجموعة حصرية من منتجات العناية بالبشرة المصنوعة يدويًا، المحتفى بها في جميع أنحاء العالم لأصالتها وجودتها، وتجسد التراث الخالد للحرفية اللبنانية.',
    ourValues: 'قيمنا',
    organicTitle: 'عضوي',
    organicDescription: 'مكونات طبيعية 100%، بدون مواد كيميائية أو مواد حافظة',
    heritageTitle: 'التراث',
    heritageDescription: 'أكثر من 600 عام من الحرفية اللبنانية التقليدية',
    sustainableTitle: 'مستدام',
    sustainableDescription: 'إنتاج صديق للبيئة ومصادر مسؤولة',
    ourMission: 'مهمتنا',
    missionText1: 'في كايفون كلوب، نؤمن بقوة الطبيعة. مهمتنا هي تقديم أفضل منتجات العناية بالبشرة العضوية من أعرق علامة تراثية لبنانية، خان الصابون. كل منتج يروي قصة التقاليد والنقاء وطقوس الجمال الخالدة المتوارثة عبر الأجيال.',
    missionText2: 'نحن ملتزمون بالاستدامة والأصالة وتقديم الفخامة التي تحترم بشرتك وكوكبنا.',
    emailLabel: 'البريد الإلكتروني',
    phoneLabel: 'الهاتف',
    addressLabel: 'العنوان',
    businessHours: 'ساعات العمل',
    mondayFriday: 'الإثنين - الجمعة',
    saturday: 'السبت',
    sunday: 'الأحد',
    closed: 'مغلق',
    whyOrganic: 'لماذا عضوي؟',
    organicBenefits: 'منتجاتنا مصنوعة من مكونات طبيعية 100%، خالية من المواد الكيميائية والمواد الحافظة. كل منتج مصنوع يدويًا بشغف وتفانٍ.',
    filterByCategory: 'تصفية حسب الفئة',
    filterByPrice: 'تصفية حسب السعر',
    sortBy: 'ترتيب حسب',
    priceRange: 'نطاق السعر',
    productsFound: 'منتج تم العثور عليه',
    addedToCart: 'تمت الإضافة إلى السلة',
    ingredients: 'المكونات',
    benefits: 'الفوائد',
    howToUse: 'طريقة الاستخدام',
    scentProfile: 'ملف العطر',
    productDescription: 'وصف المنتج',
    categories: 'الفئات',
    all: 'الكل',
    soaps: 'صابون',
    oils: 'زيوت',
    serums: 'سيروم',
    bodyCare: 'العناية بالجسم',
    hairCare: 'العناية بالشعر',
    giftSets: 'مجموعات هدايا',
    contactUs: 'اتصل بنا',
    getInTouch: 'نحب أن نسمع منك. أرسل لنا رسالة وسنعود إليك في أقرب وقت ممكن.',
    name: 'الاسم',
    email: 'البريد الإلكتروني',
    message: 'الرسالة',
    send: 'إرسال',
    followUs: 'تابعنا',
    customerService: 'خدمة العملاء',
    information: 'معلومات',
    privacyPolicy: 'سياسة الخصوصية',
    termsConditions: 'الشروط والأحكام',
    returnRefund: 'الإرجاع والاسترداد',
    cookiePolicy: 'سياسة ملفات تعريف الارتباط',
    allRightsReserved: 'جميع الحقوق محفوظة',
    // 404 Page
    pageNotFound: 'الصفحة غير موجودة',
    pageNotFoundMessage: 'عذرًا! الصفحة التي تبحث عنها غير موجودة.',
    returnToHome: 'العودة إلى الصفحة الرئيسية',
    // Accessibility
    openCart: 'فتح السلة',
    openMenu: 'فتح القائمة',
    closeMenu: 'إغلاق القائمة',
    changeLanguage: 'تغيير اللغة',
    searchProducts: 'البحث عن المنتجات',
    previousImage: 'الصورة السابقة',
    nextImage: 'الصورة التالية',
    closeDialog: 'إغلاق',
    previousPage: 'الصفحة السابقة',
    nextPage: 'الصفحة التالية',
    goToPage: 'اذهب إلى الصفحة',
    cookieConsent: 'موافقة ملفات تعريف الارتباط',
    // Admin translations
    adminPanel: 'لوحة الإدارة',
    storeManager: 'مدير المتجر',
    limitedAccess: 'وصول محدود',
    dashboard: 'لوحة التحكم',
    products: 'المنتجات',
    stockManager: 'إدارة المخزون',
    ordersMenu: 'الطلبات',
    orders: {
      title: 'إدارة الطلبات',
      searchPlaceholder: 'ابحث عن رقم الطلب أو البريد الإلكتروني...',
      allStatuses: 'جميع الحالات',
      statusPending: 'قيد الانتظار',
      statusInProgress: 'جاري التنفيذ',
      statusShipped: 'تم الشحن',
      fetchError: 'فشل تحميل الطلبات',
      statusUpdated: 'تم تحديث حالة الطلب',
      updateError: 'فشل تحديث الحالة',
      noOrders: 'لا توجد طلبات',
      orderNumber: 'رقم الطلب',
      customer: 'العميل',
      total: 'المجموع',
      status: 'الحالة',
      viewDetails: 'عرض التفاصيل',
      createdAt: 'تاريخ الإنشاء',
      orderDetails: 'تفاصيل الطلب',
      customerInfo: 'معلومات العميل',
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      address: 'العنوان',
      items: 'المنتجات',
      product: 'المنتج',
      quantity: 'الكمية',
      price: 'السعر',
      subtotal: 'المجموع الفرعي',
    },
    banner: 'البانر',
    siteInfo: 'معلومات الموقع',
    userManagement: 'إدارة المستخدمين',
    homepage: 'الصفحة الرئيسية',
    signOut: 'تسجيل الخروج',
    // Stock Manager
    manageInventory: 'إدارة المخزون ومستويات المخزون',
    monitorManageInventory: 'مراقبة وإدارة مستويات مخزون المنتجات',
    totalProducts: 'إجمالي المنتجات',
    lowStock: 'مخزون منخفض',
    updateStock: 'تحديث المخزون',
    currentStock: 'المخزون الحالي',
    lowStockThreshold: 'حد المخزون المنخفض',
    save: 'حفظ',
    // User Management
    manageAdminsStoreManagers: 'إدارة المديرين ومديري المتجر',
    addUser: 'إضافة مستخدم',
    admins: 'المديرون',
    storeManagers: 'مديرو المتجر',
    added: 'مضاف',
    resetPassword: 'إعادة تعيين كلمة المرور',
    accessLevels: 'مستويات الوصول',
    adminsAccessDesc: 'وصول كامل لجميع الميزات بما في ذلك إدارة المستخدمين وإعدادات الموقع وإدارة المحتوى.',
    storeManagersAccessDesc: 'يمكن إدارة المنتجات والطلبات والمخزون. لا يمكن الوصول إلى إدارة المستخدمين أو إعدادات الموقع.',
    protected: 'محمي',
    // Banner Manager
    bannerManager: 'مدير البانر',
    managePromotionalBanner: 'إدارة بانر الترويج',
    bannerSettings: 'إعدادات البانر',
    enableBanner: 'تفعيل البانر',
    bannerImageUrl: 'رابط صورة البانر',
    bannerText: 'نص البانر',
    enterBannerText: 'أدخل نص البانر',
    // Site Information
    siteInformation: 'معلومات الموقع',
    manageContactSocialInfo: 'إدارة معلومات الاتصال ووسائل التواصل الاجتماعي',
    contactInformation: 'معلومات الاتصال',
    whatsappNumber: 'رقم واتساب',
    socialMediaLinks: 'روابط وسائل التواصل الاجتماعي',
    // Product Guide Assistant
    smartProductGuide: 'مستشار المنتجات الذكي',
    smartProductGuideText: 'دع مساعدنا الذكي يساعدك في اختيار المنتجات الأنسب لبشرتك وشعرك وتفضيلاتك.',
    startSmartProductGuide: 'ابدأ مستشار المنتجات',
    resetChat: 'إعادة تعيين المحادثة',
    typeYourMessage: 'اكتب رسالتك...',
    // Common
    allOrders: 'جميع الطلبات',
    common: {
      loading: 'جاري التحميل...',
      sending: 'جاري الإرسال...',
      backToHome: 'العودة للرئيسية',
    },
    noOrdersFound: 'لم يتم العثور على طلبات',
    placedOn: 'تم الطلب في',
    customerInformation: 'معلومات العميل',
    orderInformation: 'معلومات الطلب',
    saveChanges: 'حفظ التغييرات',
    // Order Tracking
    trackYourOrder: 'تتبع طلبك',
    trackYourOrderDescription: 'أدخل بريدك الإلكتروني ورقم الطلب لعرض تفاصيل الطلب',
    enterOrderDetails: 'أدخل تفاصيل الطلب',
    enterOrderDetailsDescription: 'نحتاج إلى بريدك الإلكتروني ورقم الطلب للعثور على طلبك',
    orderNumberPlaceholder: 'مثال: ORD-12345',
    emailPlaceholder: 'your@email.com',
    trackOrder: 'تتبع الطلب',
    orderNotFound: 'الطلب غير موجود',
    orderNotFoundDescription: 'لم يتم العثور على طلب بهذا البريد الإلكتروني ورقم الطلب. يرجى التحقق من صحة المعلومات.',
    orderFound: 'تم العثور على الطلب',
    orderFoundDescription: 'تم العثور على طلبك وتحميله',
    trackOrderError: 'حدث خطأ أثناء تتبع الطلب',
    orderStatus: 'حالة الطلب',
    orderDate: 'تاريخ الطلب',
    contactAboutOrder: 'اتصل بنا بخصوص هذا الطلب',
    contactAboutOrderDescription: 'هل لديك أسئلة حول طلبك؟ أرسل لنا رسالة.',
    sendMessage: 'إرسال رسالة',
    subject: 'الموضوع',
    subjectPlaceholder: 'مثال: سؤال حول التسليم',
    messagePlaceholder: 'اكتب رسالتك هنا...',
    optional: 'اختياري',
    messageSent: 'تم إرسال الرسالة',
    messageSentDescription: 'لقد تلقينا رسالتك وسنعود إليك في أقرب وقت ممكن.',
    messageSendError: 'تعذر إرسال الرسالة',
    contactMessageRequired: 'لا يمكن أن تكون الرسالة فارغة',
    trackOrderFillAllFields: 'يرجى ملء البريد الإلكتروني ورقم الطلب',
    invalidEmailFormat: 'عنوان بريد إلكتروني غير صالح',
    orderArchivedMessage: 'هذا الطلب مؤرشف أو أقدم من 30 يومًا. للحصول على الدعم، يرجى الاتصال بنا مباشرة على support@caivonclub.se',
    currency: 'ك.س',
    error: 'خطأ',
    // Reviews
    reviews: {
      leaveReview: 'اترك تقييم',
      reviewingProduct: 'أنت تقيم',
      yourRating: 'تقييمك',
      yourComment: 'تعليقك',
      commentPlaceholder: 'أخبرنا عن تجربتك مع المنتج...',
      charactersMin: 'أحرف كحد أدنى',
      submitReview: 'إرسال التقييم',
      thankYou: 'شكراً لتقييمك!',
      submitError: 'تعذر إرسال التقييم',
      selectRating: 'يرجى اختيار تقييم',
      commentTooShort: 'يجب أن يكون التعليق 10 أحرف على الأقل',
      invalidLink: 'رابط تقييم غير صالح',
      invalidLinkDesc: 'رابط التقييم هذا غير صالح أو تم استخدامه بالفعل.',
      verifiedPurchase: 'عملية شراء موثقة',
      customerReviews: 'تقييمات العملاء',
      noReviews: 'لا توجد تقييمات بعد',
      beFirst: 'كن أول من يقيم هذا المنتج!',
      basedOn: 'بناءً على',
      review: 'تقييم',
      reviewsPlural: 'تقييمات',
    },
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>('sv');

  const t = (key: string): string => {
    // Support nested keys like 'orders.title'
    const keys = key.split('.');
    let value: any = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key; // Return the key itself if path not found
      }
    }
    
    // If final value is not a string, return the key
    return typeof value === 'string' ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      <div dir={language === 'ar' ? 'rtl' : 'ltr'} className={language === 'ar' ? 'font-arabic' : ''}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};